import { inject, Injectable, InjectionToken, OnDestroy } from '@angular/core';
import {
  BookedDraft,
  bookedKey,
  isBookedExpired,
  parseBookedDraft,
  serializeBookedDraft,
  touchedAtOf,
} from './booked-draft';
import { EditDraft, editKey, isEditExpired, parseEditDraft, serializeEditDraft } from './edit-draft';
import {
  DraftKind,
  DRAFT_TTL_MS,
  draftKey,
  isEditorKey,
  isExpired,
  newDraft,
  OrderDraft,
  parseDraft,
  parseDraftKey,
  serializeDraft,
} from './order-draft';

/** The tab's own storage — a draft never leaves the tab it was written in (a closed tab takes it along). */
export const ORDER_DRAFT_STORAGE = new InjectionToken<Storage>('ORDER_DRAFT_STORAGE', {
  providedIn: 'root',
  factory: () => browserSession(),
});

/** A write is held back this long: typing a phone number or ticking a store must not write the storage on every key. */
export const DRAFT_WRITE_DELAY_MS = 500;

/**
 * The `sessionStorage` of the tab, or — when the browser forbids it (private mode, a blocked storage) — a store in memory, so that the form
 * still works and only the reload no longer brings it back.
 */
function browserSession(): Storage {
  try {
    const probe = '__cc.orderEditor.probe';
    window.sessionStorage.setItem(probe, '1');
    window.sessionStorage.removeItem(probe);
    return window.sessionStorage;
  } catch {
    return new MemoryStorage();
  }
}

/** The storage of a tab that has none. */
export class MemoryStorage implements Storage {
  private data = new Map<string, string>();

  get length(): number {
    return this.data.size;
  }

  clear(): void {
    this.data.clear();
  }

  getItem(key: string): string | null {
    return this.data.get(key) ?? null;
  }

  key(index: number): string | null {
    return Array.from(this.data.keys())[index] ?? null;
  }

  removeItem(key: string): void {
    this.data.delete(key);
  }

  setItem(key: string, value: string): void {
    this.data.set(key, value);
  }
}

/**
 * Where the drafts of the new editor live (§5.3): `cc.orderEditor.<userId>.<kind>.<d>` in the tab's `sessionStorage`. It reads only the
 * draft of THIS operator and THIS conversation, drops what is older than 30 minutes whenever it meets it (on read, on the start of the
 * application, and whenever a new draft is made), and sweeps the keys of other operators at the start of the application and all of
 * them when the operator signs out — on a shared workplace the next operator never sees the previous one's phones and addresses.
 * Writes are held back for {@link DRAFT_WRITE_DELAY_MS}; the last one is flushed when the page goes away.
 */
@Injectable({ providedIn: 'root' })
export class OrderDraftStore implements OnDestroy {
  private readonly storage = inject(ORDER_DRAFT_STORAGE);
  private pending?: { draft: OrderDraft; kind: DraftKind } | { booked: BookedDraft; kind: 'cart' } | { edit: EditDraft; kind: 'edit' };
  private timer?: ReturnType<typeof setTimeout>;

  ngOnDestroy(): void {
    this.flush();
  }

  /** The draft of this conversation; a missing, expired, foreign or unreadable one is «none» (and an expired or unreadable one is removed). */
  load(userId: number, kind: DraftKind, id: string, nowMs: number): OrderDraft | undefined {
    const key = draftKey(userId, kind, id);
    const raw = this.read(key);
    if (raw === null) {
      return undefined;
    }
    const draft = parseDraft(raw);
    if (!draft || draft.userId !== userId || draft.id !== id || isExpired(draft, nowMs)) {
      this.remove(userId, kind, id);
      return undefined;
    }
    return draft;
  }

  /** A clean draft for a new conversation — after the stale ones are swept. */
  create(userId: number, id: string, nowMs: number): OrderDraft {
    this.purgeExpired(nowMs);
    const draft = newDraft(id, userId, nowMs);
    this.write(draft, 'new');
    return draft;
  }

  /** Held back: the last call within the delay wins. */
  schedule(draft: OrderDraft, kind: DraftKind = 'new'): void {
    this.pending = { draft, kind };
    this.arm();
  }

  // ---------- step 2: the draft of a booking (`cc.orderEditor.<userId>.cart.<cartId>`) ----------

  /** The draft of this booking; a missing, expired, foreign or unreadable one is «none» (and an expired or unreadable one is removed). */
  loadBooked(userId: number, cartId: number, nowMs: number): BookedDraft | undefined {
    const key = bookedKey(userId, cartId);
    const raw = this.read(key);
    if (raw === null) {
      return undefined;
    }
    const draft = parseBookedDraft(raw);
    if (!draft || draft.userId !== userId || draft.cartId !== cartId || isBookedExpired(draft, nowMs)) {
      this.removeBooked(userId, cartId);
      return undefined;
    }
    return draft;
  }

  /** The form of a booking goes into the storage AT ONCE (the booking was just made, the page is about to move) — what was held back for it is dropped. */
  saveBooked(draft: BookedDraft): void {
    if (this.pending && 'booked' in this.pending && this.pending.booked.cartId === draft.cartId) {
      this.pending = undefined;
    }
    this.writeBooked(draft);
  }

  /** Held back: the last call within the delay wins. */
  scheduleBooked(draft: BookedDraft): void {
    this.pending = { booked: draft, kind: 'cart' };
    this.arm();
  }

  removeBooked(userId: number, cartId: number): void {
    if (this.pending && 'booked' in this.pending && this.pending.booked.userId === userId && this.pending.booked.cartId === cartId) {
      this.pending = undefined;
    }
    try {
      this.storage.removeItem(bookedKey(userId, cartId));
    } catch {
      // a storage that cannot be written cannot hold a draft either
    }
  }

  // ---------- step 3: the draft of the change of an order (`cc.orderEditor.<userId>.edit.<orderId>`) ----------

  /** The draft of the change of this order; a missing, expired, foreign or unreadable one is «none» (and an expired or unreadable one is removed). */
  loadEdit(userId: number, orderId: number, nowMs: number): EditDraft | undefined {
    const raw = this.read(editKey(userId, orderId));
    if (raw === null) {
      return undefined;
    }
    const draft = parseEditDraft(raw);
    if (!draft || draft.userId !== userId || draft.orderId !== orderId || isEditExpired(draft, nowMs)) {
      this.removeEdit(userId, orderId);
      return undefined;
    }
    return draft;
  }

  /** Held back: the last call within the delay wins. */
  scheduleEdit(draft: EditDraft): void {
    this.pending = { edit: draft, kind: 'edit' };
    this.arm();
  }

  /** The change was saved, or the page was left: the draft is gone at once (a held-back write of it is dropped, too). */
  removeEdit(userId: number, orderId: number): void {
    if (this.pending && 'edit' in this.pending && this.pending.edit.userId === userId && this.pending.edit.orderId === orderId) {
      this.pending = undefined;
    }
    try {
      this.storage.removeItem(editKey(userId, orderId));
    } catch {
      // a storage that cannot be written cannot hold a draft either
    }
  }

  private arm(): void {
    if (this.timer === undefined) {
      this.timer = setTimeout(() => this.flush(), DRAFT_WRITE_DELAY_MS);
    }
  }

  /** Writes the held-back draft now (the page is closing, a request is about to leave). */
  flush(): void {
    if (this.timer !== undefined) {
      clearTimeout(this.timer);
      this.timer = undefined;
    }
    const pending = this.pending;
    this.pending = undefined;
    if (pending && 'booked' in pending) {
      this.writeBooked(pending.booked);
    } else if (pending && 'edit' in pending) {
      this.writeEdit(pending.edit);
    } else if (pending && 'draft' in pending) {
      this.write(pending.draft, pending.kind);
    }
  }

  /** The booking was made, the order created, the form left: the draft is gone at once (a held-back write of it is dropped, too). */
  remove(userId: number, kind: DraftKind, id: string): void {
    if (this.pending && 'draft' in this.pending && this.pending.draft.userId === userId && this.pending.draft.id === id && this.pending.kind === kind) {
      this.pending = undefined;
    }
    try {
      this.storage.removeItem(draftKey(userId, kind, id));
    } catch {
      // a storage that cannot be written cannot hold a draft either
    }
  }

  /** Every editor key whose draft is older than 30 minutes — or is not a readable draft at all — is removed (of any kind: a new order, a booking). */
  purgeExpired(nowMs: number): void {
    this.sweep(key => {
      const touchedAt = parseDraftKey(key) ? touchedAtOf(this.read(key)) : undefined;
      return touchedAt === undefined || nowMs - touchedAt >= DRAFT_TTL_MS;
    });
  }

  /** At the start of the application, once the operator is known: the keys of another operator and the stale ones go — nobody's drafts are left lying around because the editor was not opened. */
  sweepAtStart(userId: number, nowMs: number): void {
    this.purgeForeign(userId);
    this.purgeExpired(nowMs);
  }

  /** The keys of another operator go. */
  purgeForeign(userId: number): void {
    this.sweep(key => parseDraftKey(key)?.userId !== userId);
  }

  /** On sign-out: every editor key goes. */
  purgeAll(): void {
    this.pending = undefined;
    this.sweep(() => true);
  }

  private write(draft: OrderDraft, kind: DraftKind): void {
    try {
      this.storage.setItem(draftKey(draft.userId, kind, draft.id), serializeDraft(draft));
    } catch {
      // a full or forbidden storage: the form goes on without its reload-proof copy
    }
  }

  private writeEdit(draft: EditDraft): void {
    try {
      this.storage.setItem(editKey(draft.userId, draft.orderId), serializeEditDraft(draft));
    } catch {
      // a full or forbidden storage: the form goes on without its reload-proof copy
    }
  }

  private writeBooked(draft: BookedDraft): void {
    try {
      this.storage.setItem(bookedKey(draft.userId, draft.cartId), serializeBookedDraft(draft));
    } catch {
      // a full or forbidden storage: the form goes on without its reload-proof copy
    }
  }

  private read(key: string): string | null {
    try {
      return this.storage.getItem(key);
    } catch {
      return null;
    }
  }

  private sweep(drop: (key: string) => boolean): void {
    try {
      const keys: string[] = [];
      for (let i = 0; i < this.storage.length; i++) {
        const key = this.storage.key(i);
        if (key !== null && isEditorKey(key)) {
          keys.push(key);
        }
      }
      keys.filter(drop).forEach(key => this.storage.removeItem(key));
    } catch {
      // nothing to sweep in a storage that cannot be read
    }
  }
}
